import { Grid, IconButton } from "@mui/material";
import Box from "@mui/material/Box";
import Divider from "@mui/material/Divider";
import { ReactComponent as ArrowDown } from "assets/ArrowDown.svg";
import { ReactComponent as ArrowUp } from "assets/ArrowUp.svg";
import { ReactComponent as Priority } from "assets/Priority.svg";
import { ReactComponent as StatusBar } from "assets/StatusBar.svg";
import ThemeContext from "components/app/Context";
import AsyncAutocomplete from "components/common/Autocomplete";
import CloseButton from "components/common/CloseButton";
import CollapsibleContainer from "components/common/CollapsibleContainer";
import Input from "components/common/Input";
import Typography from "components/common/Typography";
import Calender from "components/common/calender/Calender";
import Dropdown from "components/common/dropdowns/DropDown";
import moment from "moment";
import React, { useContext } from "react";
import { Controller, useFormContext } from "react-hook-form";
import { useAppSelector } from "redux/hooks";
import { tokens } from "styles/Tokens";
import { statusTypes } from "utils/CommonConstants";
import { converter_ArrayString, createParamforSearch } from "utils/CommonFunctions";
import ActionButtons from "./ActionButtons";

interface Props {
  handleClose: any;
  assigneeList?: any;
  setAssigneeVal?: any;
  item?: any;
  isNew?: boolean;
  handleSubmit: (args: any) => void;
}

function MyPlanActionDetail(props: Props) {
  const {
    control,
    register,
    watch,
    formState: { errors }
  } = useFormContext();
  const isCorp = useAppSelector((state) => state.app.loggedInUserDetails.isCorp);
  const { handleClose, assigneeList, setAssigneeVal, isNew, handleSubmit } = props;
  const [openCollapseContainer, setOpenCollapseContainer] = React.useState(false);
  const activeCard: any = useAppSelector((state: any) => state.app.activeActionCard);
  const myPlanDetails: any = useAppSelector((state: any) => state.myPlanDetails.myPlanDetails);
  const isViewMode = useAppSelector((state: any) => state.myPlanDetails.isViewMode);

  const planDueDate = myPlanDetails.dueDate;
  const { isMobileScreen } = useContext(ThemeContext);
  const { status, priority, targetedMetrics, dueDate } = activeCard;

  const handleClick = () => setOpenCollapseContainer((state) => !state);

  return (
    <>
      <Divider
        orientation={isMobileScreen ? "horizontal" : "vertical"}
        sx={{ height: "inherit", marginTop: { xs: "16px", md: "none" } }}
      />
      <Grid
        display="flex"
        flexDirection="column"
        sx={{
          height: "100%",
          overflow: isMobileScreen ? "none" : "auto",
          padding: isMobileScreen ? "24px" : "16px",
          flex: "4"
        }}
      >
        {!isMobileScreen && <CloseButton handleClose={handleClose} />}
        <Grid
          gap={3}
          display="flex"
          flexDirection="column"
          justifyContent="center"
          margin={isMobileScreen ? "none" : "0px 20px"}
        >
          {!isViewMode && (
            <Grid item xs={12} display="flex" flexDirection="column" gap={2} alignItems="flex-start">
              <Box width={"100%"}>
                {" "}
                <Dropdown
                  className="actionStatusChange-inside"
                  register={register}
                  name="status"
                  startAdornment={<StatusBar />}
                  initialValue={isNew || !status ? "New" : status}
                  options={converter_ArrayString(statusTypes)}
                  label="Status"
                  sx={{ margin: "4px 0px", "& .MuiSelect-select": { marginLeft: "11.43px" } }}
                />
              </Box>
            </Grid>
          )}
          <Grid display="flex" flexDirection="column" gap={2} alignItems="flex-start">
            <Box width={"100%"}>
              <Typography variant="body2" color={tokens.Text.General.PrimaryHeadline} sx={{ marginBottom: "4px" }}>
                <Typography component="span" color={tokens.Text.Feedback.Alert}>
                  {" * "}
                </Typography>
                Assign to
              </Typography>
              <Controller
                name="assignees"
                control={control}
                rules={{
                  required: true
                }}
                // defaultValue={assigneeList}
                render={({ field: { onChange } }) => (
                  <AsyncAutocomplete
                    error={errors?.assignees}
                    placeholder="Search an user"
                    assigneeList={watch("assignees") || assigneeList}
                    getUserDetail={(val) => setAssigneeVal(val)}
                    onChange={onChange}
                    searchParams={{ innCode: createParamforSearch(isCorp, myPlanDetails?.innCode) }}
                  />
                )}
              />
            </Box>
            <Box width={"100%"}>
              <Dropdown
                register={register}
                name="priority"
                startAdornment={<Priority />}
                initialValue={priority}
                options={converter_ArrayString(["Low", "Medium", "High"])}
                label=" Priority"
                sx={{ margin: "4px 0px", "& .MuiSelect-select": { marginLeft: "11.43px" } }}
              />
            </Box>
            <Controller
              name="dueDate"
              control={control}
              rules={{
                validate: (dueDate) => {
                  if (dueDate && planDueDate) {
                    return (
                      moment(dueDate).isBefore(moment(planDueDate)) ||
                      moment(dueDate).isSame(moment(planDueDate)) ||
                      "Action due date can't exceed the plan's due date."
                    );
                  }
                }
              }}
              render={({ field: { onChange, name } }) => (
                <Calender
                  onChange={onChange}
                  name={name}
                  error={errors?.dueDate}
                  defaultValue={dueDate || (isNew ? planDueDate || null : null)}
                />
              )}
            />
          </Grid>
          <Grid display="flex" justifyContent="center" flexWrap="wrap">
            <Typography color={tokens.Text.General.PrimaryHeadline} variant="body2">
              Advanced
            </Typography>
            <Divider sx={{ flexGrow: "1", margin: "0 5px 10px 5px" }} />
            <IconButton aria-label="delete" onClick={handleClick}>
              {openCollapseContainer ? <ArrowUp /> : <ArrowDown />}
            </IconButton>
          </Grid>
          <CollapsibleContainer isOpen={openCollapseContainer} sx={{ width: "100%" }}>
            <Grid display="flex" alignItems="flex-start" flexWrap="wrap">
              <Input
                register={register}
                name="targetedMetrics"
                label="Targeted Metrics"
                textVariant="body2"
                color={tokens.Text.General.PrimaryHeadline}
                value={isNew ? "" : targetedMetrics}
              />
            </Grid>
          </CollapsibleContainer>
        </Grid>
        <Grid display="flex" justifyContent="center" flexWrap="wrap">
          <ActionButtons handleClose={handleClose} handleSubmit={handleSubmit} isMyPlan />
        </Grid>
      </Grid>
    </>
  );
}
export default MyPlanActionDetail;
